/// <reference types="@angular/localize" />

import { bootstrapApplication } from '@angular/platform-browser';
import { appConfig } from './app/app.config';
import { AppComponent } from './app/app.component';
import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import { installNativeTransactionStorage } from './app/core/auth/native-transaction-storage';

// Make the Auth0 login transaction survive the iOS SFSafariViewController
// round-trip by backing sessionStorage with localStorage on native. MUST run
// before the Auth0 client is created / any login starts (see the module doc).
installNativeTransactionStorage();

// Add imports for locale registration
import { registerLocaleData } from '@angular/common';

// Import locale data for all supported languages
import localeEs from '@angular/common/locales/es';
import localeEn from '@angular/common/locales/en';
import localeFr from '@angular/common/locales/fr';
import localeIt from '@angular/common/locales/it';
import localePt from '@angular/common/locales/pt';
import localeDe from '@angular/common/locales/de';
import localeKa from '@angular/common/locales/ka'; // Georgian
import localeEl from '@angular/common/locales/el'; // Greek
import localeHu from '@angular/common/locales/hu';
import localeTr from '@angular/common/locales/tr';
import localeUk from '@angular/common/locales/uk'; // Ukrainian
import localeSv from '@angular/common/locales/sv'; // Swedish
import localeDa from '@angular/common/locales/da'; // Danish
import localePl from '@angular/common/locales/pl';
import localeNl from '@angular/common/locales/nl';
import localeLt from '@angular/common/locales/lt';
import localeLv from '@angular/common/locales/lv';
import localeFi from '@angular/common/locales/fi'; // Finnish
import localeCs from '@angular/common/locales/cs'; // Czech

// Register all locales
registerLocaleData(localeEs, 'es');
registerLocaleData(localeEn, 'en');
registerLocaleData(localeFr, 'fr');
registerLocaleData(localeIt, 'it');
registerLocaleData(localePt, 'pt');
registerLocaleData(localeDe, 'de');
registerLocaleData(localeKa, 'ge'); // Note: Angular uses 'ka' but your app uses 'ge'
registerLocaleData(localeEl, 'gr'); // Note: Angular uses 'el' but your app uses 'gr'
registerLocaleData(localeHu, 'hu');
registerLocaleData(localeTr, 'tr');
registerLocaleData(localeUk, 'ua'); // Note: Angular uses 'uk' but your app uses 'ua'
registerLocaleData(localeSv, 'se'); // Note: Angular uses 'sv' but your app uses 'se'
registerLocaleData(localeDa, 'dk'); // Note: Angular uses 'da' but your app uses 'dk'
registerLocaleData(localePl, 'pl');
registerLocaleData(localeNl, 'nl');
registerLocaleData(localeLt, 'lt');
registerLocaleData(localeLv, 'lv');
registerLocaleData(localeFi, 'fi'); // Finnish
registerLocaleData(localeCs, 'cz'); // Note: Angular uses 'cs' but your app uses 'cz'

bootstrapApplication(AppComponent, appConfig)
  .then(() => hideNativeSplash())
  .catch((err) => console.error(err));

// Hand off from the native (Capacitor) launch splash to the app once Angular has
// bootstrapped. The native Android 12 splash shows the indigo background + the
// rettX logo (windowSplashScreenAnimatedIcon) from the very first frame and is
// held on screen (launchAutoHide:false) for the whole launch — so the ONLY logo
// the user ever sees is the native one (no WebView-paint dependency, no second
// logo, so no size jump at handoff). Here we fade the native splash out; it
// reveals a PURE INDIGO HTML overlay (no logo — see index.html) that bridges to
// the app while it paints, then dissolves into the indigo app surface. No-op on
// the web build, where the overlay is hidden before first paint.
function hideNativeSplash(): void {
  if (Capacitor?.isNativePlatform?.() === true) {
    SplashScreen.hide().catch(() => {});
  }

  const el = document.getElementById('app-splash');
  if (!el) {
    return;
  }
  // Brief hold so the app paints under the overlay, then dissolve.
  const HOLD_MS = 400;
  setTimeout(() => {
    el.classList.add('app-splash--hidden');
    setTimeout(() => el.remove(), 550);
  }, HOLD_MS);
}